import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/markdoc/tags/index.ts'
import * as nodes from '/vercel/path0/markdoc/nodes/index.ts'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer();

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: \"9️⃣ Ethereum Name Service support – SafeKeep launch countdown #9\"\ndate: 2020-06-10T00:25:15-08:00\ncategories: [\"ecosystem\"]\ndescription: \"SafeKeep shows you all of your ENS names including ones that are about to expire.\"\ntype: legacy\n---\n\nTLDR; With the official public beta launch of SafeKeep, we are counting down the top 10 reasons why SafeKeep is the best and the only place to get a truly comprehensive understanding of your DeFi holdings. Up next is feature #9: Ethereum Name Service (ENS) compatibility.\n\n\n\n## Countdown #9: ENS is the critical infrastructure piece required for mass adoption\n\n\n![Image](https://www.covalenthq.com/static/images/blog/202006/9-ens.png)\n \n \n\n\n\n\n\n\nWith SafeKeep, we've built a Google-like experience and you are free to use it with either your `.eth` address, or your public address hash. We want SafeKeep to be frictionless for the sophisticated DeFi investor, and so with that in mind, we’ve integrated Ethereum Name Service (ENS) across the app.\n\nWe are big fans of ENS and believe that the adoption of crypto is dependent on human-readable names. For too long, blockchain users have had to deal with kludgy public address hashes. With SafeKeep, simply type in your `.eth` address and be on your way. Whenever possible, we should show you your ENS name rather than the address hash. This feature will be more apparent once we launch the multiple wallet support. For example, you can see the transaction view in SafeKeep (screenshot below) showing my ENS name:\n\n\n![Image](https://www.covalenthq.com/static/images/blog/202006/9-ens-tx-view.png)\n \n \n\n\n\n## Not just `.eth`\n\n\n![https://www.covalenthq.com/static/images/blog/202006/9-ens-adoption1.png](covalenthq.comhttps://www.covalenthq.com/static/images/blog/202006/9-ens-adoption1.png)\n\nWe are starting to see broad adoption of ENS across the ecosystem. A good example is [Argent Wallet](https://www.argent.xyz/) that asks you to create an ENS name for your address under the Argent namespace: **argent.xyz**.\nSafeKeep is powered by [Covalent's DeFi SDK](/blog/covalent-defi-sdk/) – so this usecase is natively supported and in line with our vision of an integrated approach to your DeFi assets.\nIf you're an Argent wallet power user, go ahead and try SafeKeep with your address. It also automatically detects your wallet type with a visual indicator keeping you organized.\n\n\nFurthermore, SafeKeep will be able to give you data that is not accessible via the Argent interface.\n\n\n## One more thing: See Expiring Names\n\nA major pain point for the DeFi power user is having one single place to see all upcoming deadlines, expiries and other dates of note. We've built a section of SafeKeep for upcoming ENS expiries.\n\nAs you can see in the screenshot below, my ENS name `ganeshswami.eth` has expired and I haven't really gotten around to getting it renewed!\n\n\n![Image](https://www.covalenthq.com/static/images/blog/202006/9-ens-expiring.png)\n \n \n\n\n## Are you on top of your expiring ENS names?\n\nWhy not partake in our public beta and give us feedback?\n\n\n\n\n## What's next?\n\n\nSafeKeep is more than just a simple block explorer. Stay tuned for reason #8 why SafeKeep is the best record-keeping app for the sophisticated DeFi investor: **Deep Transaction View**.\n\n";
const filepath = "/blog/9-ethereum-name-service-safekeep-launch.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
